
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Session, User } from '@supabase/supabase-js';
import { toast } from '@/utils/toast';
import { typedSupabaseTable, typedRpcFunction } from '@/utils/supabaseTypes';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  userRole: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  userRole: null,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();

  const checkAdminStatus = async (userId?: string) => {
    if (!userId) {
      setIsAdmin(false);
      return;
    }

    console.log("Checking admin status for user:", userId);
    try {
      const { data, error } = await typedRpcFunction<boolean>(
        supabase, 
        'is_admin', 
        { user_id: userId }
      );
      
      if (error) {
        console.error('Error in is_admin check:', error);
        throw error;
      }
      
      console.log("Admin status result:", data);
      setIsAdmin(!!data);
      
      // Fetch user role from taxhealth_user_roles
      const { data: roleData, error: roleError } = await typedRpcFunction<string>(
        supabase,
        'get_taxhealth_role',
        { _user_id: userId }
      );
      if (!roleError && roleData) {
        console.log("User role:", roleData);
        setUserRole(roleData as string);
      } else {
        setUserRole(null);
      }
    } catch (error: any) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setUserRole(null);
    }
  };

  useEffect(() => {
    console.log("AuthProvider initializing...");

    // Set up the auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state change:", event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user?.id) {
          // Keep loading true until role is fetched to prevent ProtectedRoute from denying access
          setLoading(true);
          setTimeout(async () => {
            await checkAdminStatus(session.user.id);
            setLoading(false);
          }, 0);
        } else {
          setIsAdmin(false);
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    // Then check for an existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log("Initial session check:", session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user?.id) {
        await checkAdminStatus(session.user.id);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log("Signing in user:", email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      console.log("Sign in successful:", data.user?.id);
      
      // Wait for admin status check before redirecting
      if (data.user) {
        await checkAdminStatus(data.user.id);
        // Redirect is handled by Login.tsx based on role
      }
    } catch (error: any) {
      toast.error(`Erro ao fazer login: ${error.message}`);
      console.error('Error signing in:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      console.log("Signing up user:", email);
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      toast.success('Conta criada com sucesso! Verifique seu email para confirmar.');
    } catch (error: any) {
      toast.error(`Erro ao criar conta: ${error.message}`);
      console.error('Error signing up:', error);
    }
  };

  const signOut = async () => {
    try {
      console.log("Signing out user");
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setUserRole(null);
      
      navigate('/login');
      toast.success('Sessão encerrada com sucesso');
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error(`Erro ao sair: ${error.message}`);
      
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setUserRole(null);
      
      navigate('/login');
    }
  };

  const contextValue = {
    user,
    session,
    loading,
    isAdmin,
    userRole,
    signIn,
    signUp,
    signOut
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
